import { useState } from 'react'
import { Link } from 'react-router-dom'
import api from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { AuthShell, EASE_OUT_STRONG } from '@/components/auth/AuthShell'
import { Mail, ArrowRight, AlertTriangle } from 'lucide-react'

export default function AffiliateApplyPage() {
  const { toast } = useToast()
  const [submitted, setSubmitted] = useState(false)
  const [submittedMessage, setSubmittedMessage] = useState('')
  const [alreadyExists, setAlreadyExists] = useState<{ status: string; createdAt: string } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    website: '',
    audience: '',
    channel: '',
    notes: '',
  })

  const update = (key: keyof typeof form, value: string) => setForm(prev => ({ ...prev, [key]: value }))

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setAlreadyExists(null)
    try {
      const response = await api.post('/api/affiliates/apply', form)
      // 202 returns success:false + alreadyExists:true. 201 is a fresh
      // application. Distinguish so the user actually sees the block.
      if (response.data?.alreadyExists) {
        setAlreadyExists({
          status: response.data.existingStatus || 'pending',
          createdAt: response.data.existingCreatedAt || '',
        })
        setSubmittedMessage(response.data.message || '')
        return
      }
      setSubmittedMessage(
        response.data.message ||
          'LegalLady will review your application. If approved, you will receive dashboard access, a demo workspace, and your affiliate link.'
      )
      setSubmitted(true)
    } catch (err: any) {
      toast({
        title: 'Application failed',
        description: err?.response?.data?.message || err?.message || 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <AuthShell
        headline={<>Application<br /><span className="text-brand-periwinkle">submitted</span></>}
        sub="LegalLady reviews each application by hand. You should hear back over email within 24 hours — feel free to close this page in the meantime."
      >
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '60ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
        >
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-6 mb-6">
            <div className="flex items-center justify-center mb-4">
              <div className="h-12 w-12 rounded-full bg-brand-royal/15 border border-brand-royal/30 flex items-center justify-center">
                <Mail className="h-5 w-5 text-brand-periwinkle" strokeWidth={1.75} />
              </div>
            </div>
            <p className="text-center text-sm font-medium text-white mb-1">
              We've got your application
            </p>
            <p className="text-center text-xs text-white/55 leading-relaxed">
              {submittedMessage ||
                'LegalLady will review your application. If approved, you will receive dashboard access, a demo workspace, and your affiliate link.'}
            </p>
            <p className="text-center text-xs text-white/45 leading-relaxed mt-3 pt-3 border-t border-white/5">
              You should hear back within <span className="text-white/70 font-medium">24 hours</span>. You can close this page now — we'll email you when it's ready.
            </p>
          </div>
          {/* Demoted to a secondary text link. The big "Back to sign in"
              button was a trap on phones — applicants who happened to
              still have an active admin/firm session in Safari would tap
              it reflexively and /login's auto-redirect punted them
              straight into someone else's signed-in dashboard. Most
              applicants don't have an account anyway; the affirming
              card above is the right primary state for this page. */}
          <div className="text-center">
            <Link
              to="/login"
              className="text-sm text-white/50 hover:text-white transition-colors"
            >
              Already have an account? Sign in
            </Link>
          </div>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      headline={<>Earn with<br /><span className="text-brand-periwinkle">LegalLady</span></>}
      sub="Apply to demo LegalLady for your audience and earn recurring commission on every referred firm."
    >
      <form onSubmit={submit} className="grid gap-4">
        <div className="flex justify-end -mt-2">
          <Link to="/login" className="text-sm text-white/55 hover:text-white transition-colors">
            Sign in
          </Link>
        </div>
        {alreadyExists && (
          <div className="rounded-lg border border-amber-400/30 bg-amber-500/10 p-3.5 text-sm flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
            <div className="space-y-0.5">
              <p className="font-medium text-amber-100">
                {alreadyExists.status === 'approved' ? 'Already approved' : 'Application already received'}
              </p>
              <p className="text-xs text-amber-200/80 leading-relaxed">
                {submittedMessage}
                {alreadyExists.createdAt && (
                  <> Submitted {new Date(alreadyExists.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}.</>
                )}
              </p>
            </div>
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-white/80">Full name</Label>
            <Input
              id="name"
              value={form.name}
              onChange={e => update('name', e.target.value)}
              placeholder="Jane Doe"
              required
              autoComplete="name"
              className="bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 h-11"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-white/80">Email</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={e => update('email', e.target.value)}
              placeholder="you@company.com"
              required
              autoComplete="email"
              className="bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 h-11"
            />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="company" className="text-white/80">Company</Label>
            <Input
              id="company"
              value={form.company}
              onChange={e => update('company', e.target.value)}
              placeholder="Your agency name"
              className="bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 h-11"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="website" className="text-white/80">Website or profile</Label>
            <Input
              id="website"
              value={form.website}
              onChange={e => update('website', e.target.value)}
              placeholder="https://yourcompany.com"
              className="bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 h-11"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="audience" className="text-white/80">Audience</Label>
          <Textarea
            id="audience"
            value={form.audience}
            onChange={e => update('audience', e.target.value)}
            placeholder="Who would you introduce LegalLady to?"
            className="bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 min-h-[80px]"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="channel" className="text-white/80">Primary channel</Label>
          <Input
            id="channel"
            value={form.channel}
            onChange={e => update('channel', e.target.value)}
            placeholder="Agency clients, newsletter, events, social, direct sales…"
            className="bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 h-11"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="notes" className="text-white/80">Notes <span className="text-white/40">(optional)</span></Label>
          <Textarea
            id="notes"
            value={form.notes}
            onChange={e => update('notes', e.target.value)}
            className="bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 min-h-[64px]"
          />
        </div>
        <Button
          type="submit"
          disabled={submitting}
          className="mt-2 w-full bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 h-11 group disabled:opacity-50"
        >
          {submitting ? 'Submitting…' : <>Submit application <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" strokeWidth={2} /></>}
        </Button>
        <p className="mt-1 text-[11px] text-center text-white/40">
          By applying you agree to our Terms and Privacy Policy.
        </p>
      </form>
    </AuthShell>
  )
}
